import React from 'react'
import isEqual from 'lodash/isEqual'
import { i18n } from '../../index'
import { alphaSort, removeTypename } from '../../../common/utils'
import { Mutation } from 'react-apollo'
import { Table, TableBody, TableRow, TableCell, TableHead, TextField } from '@mui/material'
import { fetchEfficiencyFactors, upsertEfficiencyFactors } from '../../station/stationQueries'
import { efficiencyFactorMonths, getEfficiencyFactorForMonth } from './stationHelpers'
import type { EfficiencyFactorGroup, EfficiencyFactorMonth } from '../../station/stationTypes'
import css from './efficiencyFactorTable.css'
type EfficiencyFactorRowProps = {
  data: EfficiencyFactorGroup
  onChange: (...args: Array<any>) => any
  onBlur: (...args: Array<any>) => any
}

const updateCacheOnUpdate =
  (variables: any, updatedId: EfficiencyFactorGroup['id']) =>
  (cache: any, { data: { upsertEfficiencyFactors } }: any) => {
    const query = fetchEfficiencyFactors
    const fieldName = 'efficiencyFactors'
    const data = cache.readQuery({
      query,
      variables,
    })
    cache.writeQuery({
      query,
      variables,
      data: {
        [fieldName]: data[fieldName].map((ts) => {
          return ts.id === updatedId ? { ...ts, [fieldName]: upsertEfficiencyFactors } : ts
        }),
      },
    })
  }

const EfficiencyFactorRow = ({ data, onChange, onBlur }: EfficiencyFactorRowProps) => (
  <TableRow>
    <TableCell className={css.tableCell}>
      {data.type !== 'Station' ? data.name : i18n.t('admin.stations.label.efficiencyFactorsStation')}
    </TableCell>
    {efficiencyFactorMonths.map((month) => (
      <TableCell className={css.tableCellNumeric} key={month}>
        <TextField
          type='number'
          value={getEfficiencyFactorForMonth(month, data.efficiencyFactors || []) || ''}
          onBlur={() => onBlur(data)}
          onKeyPress={(e) => (e.key === 'Enter' ? onBlur(data) : null)}
          onChange={(e) => onChange(data, month, Number(e.target.value))}
        />
      </TableCell>
    ))}
  </TableRow>
)

type EfficiencyFactorTableProps = {
  data: Array<EfficiencyFactorGroup>
  stationId: string
}
type EfficiencyFactorTableState = {
  data: Array<EfficiencyFactorGroup>
}

class EfficiencyFactorTable extends React.PureComponent<EfficiencyFactorTableProps, EfficiencyFactorTableState> {
  state = {
    data: this.props.data,
  }

  componentDidUpdate(prevProps: EfficiencyFactorTableProps) {
    if (!isEqual(this.props.data, prevProps.data)) {
      this.setState({
        data: this.props.data,
      })
    }
  }

  onChange = (item: EfficiencyFactorGroup, month: EfficiencyFactorMonth, newValue: number) => {
    const efficiencyFactors = [
      {
        month,
        efficiencyFactor: newValue,
      },
    ]

    if (item && item.efficiencyFactors) {
      item.efficiencyFactors.forEach((ef) => {
        if (ef.month !== month) {
          efficiencyFactors.push(removeTypename(ef))
        }
      })
    }

    this.setState({
      data: this.state.data.map((dataItem) => (dataItem.id === item.id ? { ...item, efficiencyFactors } : dataItem)),
    })
  }
  onSubmit = (onMutate: (...args: Array<any>) => any, item: EfficiencyFactorGroup) => {
    const { id, efficiencyFactors } = item
    const { stationId } = this.props
    onMutate({
      variables: {
        id,
        efficiencyFactors,
      },
      update: updateCacheOnUpdate(
        {
          stationId,
        },
        id,
      ),
    })
  }

  render() {
    const { data } = this.state
    return (
      <Table>
        <TableHead>
          <TableRow className={css.tableHeadingRow}>
            <TableCell>{i18n.t('admin.stations.label.profitCenterName')}</TableCell>
            {efficiencyFactorMonths.map((month) => (
              <TableCell className={css.tableCellNumeric} key={month}>
                {String(month).length < 2 ? '0' + month : month}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {alphaSort(['type', 'name'], data).map((item) => (
            <Mutation key={item.id} mutation={upsertEfficiencyFactors}>
              {(onMutate) => (
                <EfficiencyFactorRow
                  data={item}
                  onChange={this.onChange}
                  onBlur={(item) => this.onSubmit(onMutate, item)}
                />
              )}
            </Mutation>
          ))}
        </TableBody>
      </Table>
    )
  }
}

export default EfficiencyFactorTable
